/**
 * Pure helpers for the Salesforce OAuth Web Server flow. Kept in their own
 * module so unit tests can exercise URL construction + state encoding without
 * pulling in the React UI tree (which transitively imports sentry.ts using
 * `import.meta.env`, breaking jest).
 */

import * as qS from 'query-string';
import shortid from 'shortid';

import { encodeObjBase64 } from '../../../../../../services/base64';

/**
 * OAuth scopes requested at authorize time. `api` grants REST/SOAP access;
 * `refresh_token` (a.k.a. `offline_access`) is what makes SF issue a refresh
 * token alongside the access token. Without the refresh-token scope, the
 * integration would die the moment the first access token expires.
 */
export const SF_SCOPES = ['api', 'refresh_token'];

/**
 * Builds the Salesforce authorize URL the admin gets redirected to.
 *
 * `state` carries the orgId (so the callback can look up which org this
 * connect is for) plus a per-request nonce + timestamp (so a replay of the
 * callback URL can be detected/expired by the BE if needed).
 */
export const buildAuthorizeUrl = (
  loginUrl: string,
  clientId: string,
  redirectUri: string,
  orgId: string
) => {
  const state = encodeObjBase64({ orgId, key: shortid(), ts: Date.now() });
  const query = {
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: SF_SCOPES.join(' '),
    state,
    // Force consent so SF returns a refresh_token even on re-connect for the
    // same SF user; without prompt=consent SF can skip refresh_token issuance.
    prompt: 'consent'
  };
  return `${loginUrl.replace(/\/$/, '')}/services/oauth2/authorize?${qS.stringify(query)}`;
};
